/* eslint-disable no-underscore-dangle */
import { useState } from "react";
import { trim } from "lodash";
import PropTypes from "prop-types";
import AddressCard from "@shared/AddressCard";
import IconButton from "@shared/v2/IconButton";
import Button from "@shared/v2/Button";
import { PencilSolidV6, TrashCanSolidV6 } from "@shared/v2/Icomoon";

import AddEditModalBody from "@shared/AddressModal/AddEditModalBody";

import { getSuggestedAddressCheck } from "../../../PersonDetail/shared/api";
import InvalidAddressModalBody from "../../../PersonDetail/components/InvalidAddressModal/InvalidAddressModalBody";
import VerifyAddressModalBody from "../../../PersonDetail/components/VerifyAddressModal/VerifyAddressModalBody";

function AddressView({ currentAddress, setPersonValue, options, addressList, showArmedForcesOptions }) {
  const { initShowAddressForm } = currentAddress;
  const [showAddressForm, setShowAddressForm] = useState(initShowAddressForm);
  const [showAddressCard, setShowAddressCard] = useState(!initShowAddressForm);
  const [warningAddressTypeObj, setWarningAddressTypeObj] = useState(null);
  const [showInvalidBanner, setShowInvalidBanner] = useState(false);

  const [showVerifyAddress, setShowVerifyAddress] = useState(null);
  const [isSuggestedAddress, setIsSuggestedAddress] = useState(true);

  const [initLoading, setInitLoading] = useState(false);

  const [draftAddress, setDraftAddress] = useState({
    addressType: currentAddress.addressType,
    city: currentAddress.city,
    postalCode: currentAddress.postalCode,
    streetAddress: currentAddress.streetAddress,
    locality: currentAddress.locality,
    country: currentAddress.country,
    secondaryAddress: currentAddress.secondaryAddress,
  });

  const updateDraft = (fields) => setDraftAddress((prev) => ({ ...prev, ...fields }));

  const onRemove = (targetAddress) => {
    let filteredAddressList = addressList;
    if (Object.hasOwnProperty.call(targetAddress, "id")) {
      filteredAddressList = addressList.map((address) =>
        address === targetAddress ? { ...address, _destroy: true } : address,
      );
    } else {
      filteredAddressList = addressList.filter((address) => address !== targetAddress);
    }

    setPersonValue({ key: "addressesAttributes", value: filteredAddressList });
  };

  const updateAddress = (targetAddress, value) =>
    setPersonValue({
      key: "addressesAttributes",
      value: addressList.map((address) => {
        if (address !== targetAddress) return address;
        return { ...address, ...value };
      }),
    });

  return currentAddress._destroy ? null : (
    <div className="tw-border-1px tw-border-solid tw-border-gray-10 tw-border-l-0 tw-border-t-0 tw-border-r-0 tw-pb-24px">
      {showAddressCard && (
        <div className="tw-flex tw-justify-between tw-items-center">
          <AddressCard
            addressType={currentAddress.addressType}
            streetAddress={currentAddress.streetAddress}
            cityStateZip={`${currentAddress.city}, ${currentAddress.locality} ${currentAddress.postalCode}`}
            standardized={false}
            secondaryAddress={currentAddress.secondaryAddress}
          />
          <div className="tw-flex tw-gap-8px">
            <IconButton
              schema="tertiary"
              onClick={() => {
                setShowAddressForm(true);
                setShowAddressCard(false);
              }}
            >
              <PencilSolidV6 />
            </IconButton>
            <IconButton
              schema="tertiary"
              onClick={() => {
                onRemove(currentAddress);
              }}
            >
              <TrashCanSolidV6 />
            </IconButton>
          </div>
        </div>
      )}
      {showAddressForm && (
        <div>
          <AddEditModalBody
            addressTypes={options}
            setAddressType={(newValue) => {
              const sameTypeObj = addressList.find(
                (ad) => ad.addressType === newValue && ad !== currentAddress && newValue !== "other",
              );
              setWarningAddressTypeObj(sameTypeObj || null);
              updateDraft({ addressType: newValue });
            }}
            warningAddressTypeObj={warningAddressTypeObj}
            addressType={draftAddress.addressType}
            setStreetAddress={(newValue) => updateDraft({ streetAddress: newValue })}
            streetAddress={draftAddress.streetAddress}
            secondaryAddress={draftAddress.secondaryAddress}
            setSecondaryAddress={(newValue) => updateDraft({ secondaryAddress: newValue })}
            city={draftAddress.city}
            setCity={(newValue) => updateDraft({ city: newValue })}
            setLocality={(newValue) => updateDraft({ locality: newValue })}
            locality={draftAddress.locality}
            setPostalCode={(newValue) => updateDraft({ postalCode: newValue })}
            postalCode={draftAddress.postalCode}
            country={draftAddress.country}
            setCountry={(ct) => updateDraft({ country: ct })}
            updateCompleteAddress={(completedAddress) => updateDraft(completedAddress)}
            standardized
            showArmedForcesOptions={showArmedForcesOptions}
          />
          <div className="tw-flex tw-justify-end tw-mt-32px">
            <div className="tw-flex tw-gap-8px">
              <Button
                schema="tertiary"
                onClick={() => {
                  if (initShowAddressForm) {
                    setPersonValue({
                      key: "addressesAttributes",
                      value: addressList.filter((address) => address.tempId !== currentAddress.tempId),
                    });
                    return;
                  }

                  setDraftAddress({
                    addressType: currentAddress.addressType,
                    city: currentAddress.city,
                    postalCode: currentAddress.postalCode,
                    streetAddress: currentAddress.streetAddress,
                    locality: currentAddress.locality,
                    country: currentAddress.country,
                    secondaryAddress: currentAddress.secondaryAddress,
                  });
                  setShowAddressForm(false);
                  setShowAddressCard(true);
                }}
              >
                Discard
              </Button>
              <Button
                schema="primary"
                isLoading={initLoading}
                onClick={async () => {
                  setInitLoading(true);
                  const { res: resGeoCode } = await getSuggestedAddressCheck({
                    addressType: draftAddress.addressType,
                    city: draftAddress.city,
                    postalCode: draftAddress.postalCode,
                    streetAddress: draftAddress.streetAddress,
                    secondaryAddress: draftAddress.secondaryAddress,
                    locality: draftAddress.locality,
                    country: draftAddress.country,
                  });
                  setInitLoading(false);
                  if (!resGeoCode.data?.addressDetails?.standardized) {
                    if (trim(resGeoCode.data?.addressDetails?.streetAddress) === "") {
                      setShowInvalidBanner(true);
                    } else {
                      setShowVerifyAddress({
                        originalAddress: { ...draftAddress },
                        suggestedAddress: resGeoCode.data?.addressDetails,
                      });
                    }
                    setShowAddressForm(false);
                    return;
                  }

                  if (warningAddressTypeObj) {
                    updateAddress(warningAddressTypeObj, {
                      addressType: "other",
                    });
                  }

                  updateAddress(currentAddress, { ...draftAddress, initShowAddressForm: false });

                  setShowAddressForm(false);
                  setShowAddressCard(true);
                }}
              >
                Confirm
              </Button>
            </div>
          </div>
        </div>
      )}
      {showInvalidBanner && (
        <div className="tw-flex tw-flex-col tw-gap-[24px]">
          <InvalidAddressModalBody
            streetAddress={draftAddress.streetAddress}
            secondaryAddress={draftAddress.secondaryAddress}
            city={draftAddress.city}
            locality={draftAddress.locality}
            postalCode={draftAddress.postalCode}
            country={draftAddress.country}
          />
          <div className="tw-flex tw-gap-8px tw-justify-end">
            <Button
              schema="tertiary"
              onClick={() => {
                setShowAddressForm(true);
                setShowInvalidBanner(false);
              }}
            >
              Edit Address
            </Button>
            <Button
              schema="primary"
              onClick={() => {
                updateAddress(currentAddress, { ...draftAddress, initShowAddressForm: false });
                setShowInvalidBanner(false);
                setShowAddressCard(true);
              }}
            >
              Confirm
            </Button>
          </div>
        </div>
      )}
      {showVerifyAddress && (
        <>
          <VerifyAddressModalBody
            suggestedAddress={showVerifyAddress.suggestedAddress}
            originalAddress={showVerifyAddress.originalAddress}
            isSuggestedAddress={isSuggestedAddress}
            setIsSuggestedAddress={setIsSuggestedAddress}
          />
          <div className="tw-flex tw-gap-8px tw-justify-end tw-mt-16px">
            <Button
              schema="tertiary"
              onClick={() => {
                setShowAddressForm(true);
                setShowVerifyAddress(null);
              }}
            >
              Edit Address
            </Button>
            <Button
              schema="primary"
              onClick={() => {
                const selectedAddress = isSuggestedAddress
                  ? showVerifyAddress.suggestedAddress
                  : showVerifyAddress.originalAddress;
                updateAddress(currentAddress, { ...selectedAddress, initShowAddressForm: false });
                setDraftAddress((prev) => ({ ...prev, ...selectedAddress }));
                setShowVerifyAddress(null);
                setShowAddressCard(true);
              }}
            >
              Confirm
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
const addressPropType = PropTypes.shape({
  addressType: PropTypes.string,
  city: PropTypes.string,
  country: PropTypes.string,
  locality: PropTypes.string,
  secondaryAddress: PropTypes.string,
  streetAddress: PropTypes.string,
  postalCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  id: PropTypes.number,
  initShowAddressForm: PropTypes.bool,
  tempId: PropTypes.string,
  _destroy: PropTypes.bool,
});
AddressView.propTypes = {
  currentAddress: addressPropType,
  setPersonValue: PropTypes.func.isRequired,
  addressList: PropTypes.arrayOf(addressPropType),
  options: PropTypes.arrayOf(PropTypes.shape({})),
  showArmedForcesOptions: PropTypes.bool,
};

AddressView.defaultProps = {
  currentAddress: null,
  addressList: [],
  options: [],
  showArmedForcesOptions: false,
};
export default AddressView;
